import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useParticipante } from '../../../contexts/ParticipanteContext';

type TipoTransacao = 'ganho' | 'resgate' | 'bonus' | 'expiracao' | 'ajuste';
type TipoFiltro = 'todos' | TipoTransacao;

interface DetalhesPremiacao {
  meta?: string;
  produtos?: string[];
  servicos?: string[];
  valorVendido?: number;
  quantidadeAtendimentos?: number;
  periodo?: string;
  observacoes?: string;
  contrato?: string;
  numeroOS?: string;
  clienteOS?: string;
  dataOS?: string;
  // Novos campos para discriminação de produtos vendidos
  lotesPremiacao?: LotePremiacao[];
}

interface ProdutoVendido {
  produto: string;
  qtd: number;
  valorUnit: number;
  total: number;
  desconto: number;
  liquido: number;
  informacao: string;
}

interface LotePremiacao {
  metaAtingida: string;
  contrato: string;
  cliente: string;
  data: string;
  valorTotal: number;
  periodo: string;
  observacao?: string;
  produtosVendidos: ProdutoVendido[];
}

interface Transacao {
  id: number;
  data: string;
  tipo: TipoTransacao;
  descricao: string;
  pontos: number;
  xp?: number;
  saldoAnterior: number;
  saldoAtual: number;
  detalhesPremiacao?: DetalhesPremiacao;
  imutavel?: boolean;
}

const tipoConfig: Record<TipoTransacao, { label: string; icon: string; color: string; bgColor: string; borderColor: string; rowBg: string; rowHover: string }> = {
  ganho: {
    label: 'XP',
    icon: 'ri-arrow-up-circle-fill',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-l-emerald-500',
    rowBg: 'bg-emerald-50/30',
    rowHover: 'hover:bg-emerald-50/60'
  },
  resgate: {
    label: 'Resgate',
    icon: 'ri-arrow-down-circle-fill',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-l-rose-500',
    rowBg: 'bg-rose-50/30',
    rowHover: 'hover:bg-rose-50/60'
  },
  bonus: {
    label: 'Bônus',
    icon: 'ri-gift-fill',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-l-purple-500',
    rowBg: 'bg-purple-50/30',
    rowHover: 'hover:bg-purple-50/60'
  },
  ajuste: {
    label: 'Ajuste',
    icon: 'ri-edit-circle-fill',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-l-sky-500',
    rowBg: 'bg-sky-50/30',
    rowHover: 'hover:bg-sky-50/60'
  }
};

// Gerar transações baseadas nos dados reais da campanha
const gerarTransacoes = (
  campanhaId: string,
  pontosDisponiveis: number,
  pontosGanhos: number,
  pontosDistribuidos: number
): Transacao[] => {
  const transacoes: Transacao[] = [];
  const hoje = new Date();
  
  // ========== LANÇAMENTO IMUTÁVEL - META DE VENDAS VERÃO 2024 ==========
  const transacaoMetaVendasVerao2024: Transacao = {
    id: 999999,
    data: '2026-03-24T15:09:00.000Z',
    tipo: 'bonus',
    descricao: 'Meta de Vendas',
    pontos: 2000,
    saldoAnterior: pontosDisponiveis - 2000,
    saldoAtual: pontosDisponiveis,
    imutavel: true,
    detalhesPremiacao: {
      lotesPremiacao: [
        {
          metaAtingida: 'Atingir meta de vendas mensal com produtos UNIMED NATAL',
          contrato: 'CONT-2024-UNIMED',
          cliente: 'MARIA AUGUSTA PEREIRA NASCIMENTO',
          data: '28/01/2026',
          valorTotal: 2000.00,
          periodo: 'Mensal',
          observacao: 'Vendas realizadas conforme tabela detalhada abaixo',
          produtosVendidos: [
            { produto: 'UNIMED NATAL', qtd: 1, valorUnit: 500.00, total: 500.00, desconto: 0.00, liquido: 500.00, informacao: 'Proposta D 13480057190 Adesão #880' },
            { produto: 'UNIMED NATAL', qtd: 1, valorUnit: 500.00, total: 500.00, desconto: 0.00, liquido: 500.00, informacao: 'Proposta T 13480057190 Adesão #881' },
            { produto: 'UNIMED NATAL', qtd: 1, valorUnit: 500.00, total: 500.00, desconto: 0.00, liquido: 500.00, informacao: 'Proposta D 13480056673 PME #719' },
            { produto: 'UNIMED NATAL', qtd: 1, valorUnit: 500.00, total: 500.00, desconto: 0.00, liquido: 500.00, informacao: 'Proposta T 13480056673 PME #720' },
          ]
        },
        {
          metaAtingida: 'Atingir meta de vendas mensal com produtos AMIL SÃO PAULO - Segundo Lote',
          contrato: 'CONT-2024-UNIMED-B',
          cliente: 'JOÃO CARLOS SILVA SANTOS',
          data: '26/01/2026',
          valorTotal: 1850.00,
          periodo: 'Mensal',
          observacao: 'Vendas realizadas conforme tabela detalhada abaixo - Segundo lote de vendas',
          produtosVendidos: [
            { produto: 'AMIL SÃO PAULO', qtd: 1, valorUnit: 450.00, total: 450.00, desconto: 0.00, liquido: 450.00, informacao: 'Proposta D 13480063567 Adesão #965' },
            { produto: 'HAPVIDA', qtd: 1, valorUnit: 450.00, total: 450.00, desconto: 0.00, liquido: 450.00, informacao: 'Proposta T 13480063567 Adesão #966' },
            { produto: 'HAPVIDA', qtd: 1, valorUnit: 475.00, total: 475.00, desconto: 0.00, liquido: 475.00, informacao: 'Proposta D 13480063209 PME #830' },
            { produto: 'AMIL SÃO PAULO', qtd: 1, valorUnit: 475.00, total: 475.00, desconto: 0.00, liquido: 475.00, informacao: 'Proposta T 13480063209 PME #831' },
          ]
        }
      ]
    }
  };
  
  // ========== LANÇAMENTO IMUTÁVEL - BÔNUS VENDA PLANO DE SAÚDE VERÃO 2024 ==========
  const transacaoBonusPlanoSaude: Transacao = {
    id: 999998,
    data: '2026-03-15T10:30:00.000Z',
    tipo: 'bonus',
    descricao: 'Bônus por Venda de Plano de Saúde — Campanha Verão 2024',
    pontos: 1500,
    saldoAnterior: pontosDisponiveis - 2000 - 1500,
    saldoAtual: pontosDisponiveis - 2000,
    imutavel: true,
    detalhesPremiacao: {
      lotesPremiacao: [
        {
          metaAtingida: 'Venda de Planos de Saúde — Campanha Verão 2024',
          contrato: 'CONT-2024-SAUDE-V01',
          cliente: 'ANA PAULA RODRIGUES FERREIRA',
          data: '15/11/2025',
          valorTotal: 1500.00,
          periodo: 'Mensal',
          observacao: 'Bônus referente à venda de planos de saúde conforme tabela detalhada abaixo',
          produtosVendidos: [
            { produto: 'PLANO DE SAÚDE INDIVIDUAL', qtd: 1, valorUnit: 380.00, total: 380.00, desconto: 0.00, liquido: 380.00, informacao: 'Proposta D 13480071001 Adesão #1101 — Titular: Ana Paula R. Ferreira' },
            { produto: 'PLANO DE SAÚDE FAMILIAR', qtd: 1, valorUnit: 620.00, total: 620.00, desconto: 0.00, liquido: 620.00, informacao: 'Proposta T 13480071002 Adesão #1102 — Titular: Carlos Eduardo M. Lima' },
            { produto: 'PLANO DE SAÚDE EMPRESARIAL', qtd: 1, valorUnit: 500.00, total: 500.00, desconto: 0.00, liquido: 500.00, informacao: 'Proposta D 13480071003 PME #940 — Empresa: Construtora Horizonte Ltda' },
          ]
        }
      ]
    }
  };

  // Adicionar transação imutável
  transacoes.push(transacaoMetaVendasVerao2024);
  transacoes.push(transacaoBonusPlanoSaude);
  // ========== FIM DO LANÇAMENTO IMUTÁVEL ==========
  
  // Calcular pontos gastos (distribuídos - disponíveis)
  const pontosGastos = pontosDistribuidos - pontosDisponiveis;
  
  // Templates de transações por tipo com detalhes de premiação
  const templatesGanho = [
    { 
      descricao: 'Missão Concluída: Check-in Diário', 
      pontos: 50, 
      xp: 100,
      detalhesPremiacao: {
        meta: 'Realizar check-in diário no sistema',
        periodo: 'Diário',
        observacoes: 'Presença confirmada e registrada com sucesso'
      }
    },
    { 
      descricao: 'Missão Concluída: Meta de Vendas Diária', 
      pontos: 150, 
      xp: 300,
      detalhesPremiacao: {
        meta: 'Atingir R$ 5.000 em vendas no dia',
        produtos: ['Notebook Dell Inspiron', 'Mouse Logitech MX Master', 'Teclado Mecânico'],
        valorVendido: 5420,
        periodo: 'Diário',
        contrato: 'CONT-2024-0847',
        clienteOS: 'Tech Solutions Ltda'
      }
    },
    { 
      descricao: 'Jogo Concluído: Quiz de Produtos', 
      pontos: 75, 
      xp: 150,
      detalhesPremiacao: {
        meta: 'Acertar 80% das questões sobre produtos',
        observacoes: 'Pontuação: 9/10 questões corretas'
      }
    },
    { 
      descricao: 'Missão Concluída: Conquistador Semanal', 
      pontos: 300, 
      xp: 600,
      detalhesPremiacao: {
        meta: 'Atingir R$ 25.000 em vendas na semana',
        produtos: ['Smart TV 55"', 'Soundbar Samsung', 'Smartphone Galaxy S23', 'Tablet iPad'],
        valorVendido: 27850,
        periodo: 'Semanal',
        contrato: 'CONT-2024-0891',
        clienteOS: 'Varejo Premium S.A.'
      }
    },
    { 
      descricao: 'Jogo Concluído: Memória de Clientes', 
      pontos: 50, 
      xp: 100,
      detalhesPremiacao: {
        meta: 'Completar o jogo da memória em menos de 2 minutos',
        observacoes: 'Tempo: 1min 45s - Novo recorde pessoal!'
      }
    },
    { 
      descricao: 'Missão Concluída: Atendimentos do Dia', 
      pontos: 100, 
      xp: 200,
      detalhesPremiacao: {
        meta: 'Realizar 15 atendimentos com avaliação positiva',
        servicos: ['Consultoria de produtos', 'Suporte técnico', 'Demonstração de equipamentos'],
        quantidadeAtendimentos: 18,
        periodo: 'Diário',
        numeroOS: 'OS-2024-3456',
        clienteOS: 'Diversos clientes',
        dataOS: '15/01/2024'
      }
    },
    { 
      descricao: 'Distintivo Conquistado: Vendedor Estrela', 
      pontos: 200, 
      xp: 400,
      detalhesPremiacao: {
        meta: 'Superar meta mensal em 20%',
        valorVendido: 96000,
        observacoes: 'Meta: R$ 80.000 | Realizado: R$ 96.000 (120%)',
        contrato: 'CONT-2024-0923'
      }
    },
    { 
      descricao: 'Jogo Concluído: Puzzle de Vendas', 
      pontos: 125, 
      xp: 250,
      detalhesPremiacao: {
        meta: 'Completar todos os níveis do puzzle',
        observacoes: 'Todos os 12 níveis concluídos com 3 estrelas'
      }
    },
    { 
      descricao: 'Missão Concluída: Jogador Dedicado', 
      pontos: 250, 
      xp: 500,
      detalhesPremiacao: {
        meta: 'Completar 20 jogos no mês',
        observacoes: '23 jogos completados com média de 85% de acertos'
      }
    },
    { 
      descricao: 'Missão Concluída: Social Butterfly', 
      pontos: 200, 
      xp: 400,
      detalhesPremiacao: {
        meta: 'Indicar 5 novos clientes',
        servicos: ['Indicação de clientes', 'Networking', 'Expansão de carteira'],
        quantidadeAtendimentos: 7,
        observacoes: '7 indicações convertidas em vendas',
        numeroOS: 'OS-2024-3789',
        dataOS: '18/01/2024'
      }
    },
    { 
      descricao: 'Jogo Concluído: Simulador de Atendimento', 
      pontos: 100, 
      xp: 200,
      detalhesPremiacao: {
        meta: 'Resolver 10 casos de atendimento com excelência',
        observacoes: 'Avaliação média: 9.5/10 - Excelente!'
      }
    },
    { 
      descricao: 'Missão Concluída: Vendedor Fenomenal', 
      pontos: 600, 
      xp: 1200,
      detalhesPremiacao: {
        meta: 'Atingir R$ 50.000 em vendas na semana',
        produtos: ['Geladeira Brastemp', 'Fogão Electrolux', 'Micro-ondas', 'Lavadora de Roupas', 'Ar Condicionado'],
        valorVendido: 52300,
        periodo: 'Semanal',
        observacoes: 'Superou a meta em 4.6%',
        contrato: 'CONT-2024-1002',
        clienteOS: 'Eletro Casa Ltda'
      }
    },
    { 
      descricao: 'Jogo Concluído: Caça ao Tesouro', 
      pontos: 90, 
      xp: 180,
      detalhesPremiacao: {
        meta: 'Encontrar todos os tesouros escondidos',
        observacoes: '15/15 tesouros encontrados - Recompensa completa!'
      }
    },
    { 
      descricao: 'Missão Concluída: Colecionador', 
      pontos: 400, 
      xp: 800,
      detalhesPremiacao: {
        meta: 'Conquistar 10 distintivos diferentes',
        observacoes: '12 distintivos conquistados - Coleção completa!'
      }
    },
    { 
      descricao: 'Distintivo Conquistado: Mestre do Suporte', 
      pontos: 180, 
      xp: 360,
      detalhesPremiacao: {
        meta: 'Resolver 50 tickets de suporte com avaliação 5 estrelas',
        servicos: ['Suporte técnico', 'Resolução de problemas', 'Atendimento pós-venda'],
        quantidadeAtendimentos: 53,
        observacoes: 'Avaliação média: 4.9/5 estrelas',
        numeroOS: 'OS-2024-4123',
        dataOS: '22/01/2024'
      }
    },
    { 
      descricao: 'Missão Concluída: Cliente VIP', 
      pontos: 800, 
      xp: 1600,
      detalhesPremiacao: {
        meta: 'Realizar venda acima de R$ 30.000 para cliente VIP',
        produtos: ['Home Theater Premium', 'Smart TV 75" OLED', 'Console PlayStation 5', 'Sistema de Som Bose'],
        valorVendido: 34500,
        observacoes: 'Cliente VIP fidelizado com plano de manutenção anual',
        contrato: 'CONT-2024-1045',
        clienteOS: 'Corporação Alpha S.A.'
      }
    }
  ];

  const templatesBonus = [
    { 
      descricao: 'Bônus de Boas-vindas', 
      pontos: 500,
      detalhesPremiacao: {
        meta: 'Completar cadastro e treinamento inicial',
        observacoes: 'Bem-vindo à campanha! Aproveite seus pontos iniciais'
      }
    },
    { 
      descricao: 'Bônus por Desempenho Excepcional', 
      pontos: 1000,
      detalhesPremiacao: {
        meta: 'Superar todas as metas do trimestre',
        valorVendido: 285000,
        periodo: 'Trimestral',
        observacoes: 'Desempenho 135% acima da meta estabelecida',
        contrato: 'CONT-2024-1078'
      }
    },
    { 
      descricao: 'Bônus de Aniversário da Campanha', 
      pontos: 750,
      detalhesPremiacao: {
        meta: 'Participação ativa durante 1 ano',
        observacoes: 'Parabéns por 1 ano de dedicação e resultados!'
      }
    },
    { 
      descricao: 'Bônus por Indicação de Colega', 
      pontos: 300,
      detalhesPremiacao: {
        meta: 'Indicar novo participante para a campanha',
        observacoes: 'Colega indicado completou treinamento e primeira venda'
      }
    },
    { 
      descricao: 'Bônus Especial da Gestão', 
      pontos: 500,
      detalhesPremiacao: {
        meta: 'Reconhecimento por excelência no atendimento',
        observacoes: 'Premiação especial por feedback positivo de clientes',
        numeroOS: 'OS-2024-4567',
        dataOS: '25/01/2024'
      }
    }
  ];

  const templatesResgate = [
    { descricao: 'Resgate iCash (PIX)', pontos: 2500 },
    { descricao: 'Resgate iCash (PIX)', pontos: 1800 },
    { descricao: 'Resgate iCash (PIX)', pontos: 3200 },
    { descricao: 'Resgate: Vale-Compras R$ 500', pontos: 4000 },
    { descricao: 'Resgate: AirPods Pro', pontos: 8500 },
    { descricao: 'Resgate: Jantar no Fasano', pontos: 3500 },
    { descricao: 'Resgate: Smart Watch', pontos: 6500 },
    { descricao: 'Resgate: Desconto 50% Academia', pontos: 1200 },
    { descricao: 'Resgate: Bônus 2x XP', pontos: 2000 }
  ];

  const templatesAjuste = [
    { descricao: 'Ajuste Manual: Correção de Pontos', pontos: 150 },
    { descricao: 'Ajuste Manual: Bônus Especial', pontos: 500 },
    { descricao: 'Ajuste: Correção de Sistema', pontos: -100 }
  ];

  const templatesExpiracao = [
    { descricao: 'Expiração de Pontos (90 dias)', pontos: 200 },
    { descricao: 'Expiração de Pontos (90 dias)', pontos: 150 }
  ];

  // Calcular número de transações baseado nos pontos
  let saldo = pontosDisponiveis;
  let pontosRestantesGanho = pontosGanhos;
  let pontosRestantesGasto = pontosGastos;
  let id = 1;

  // Gerar transações de ganho
  let diaAtual = 0;
  while (pontosRestantesGanho > 0 && diaAtual < 90) {
    const template = templatesGanho[Math.floor(Math.random() * templatesGanho.length)];
    const pontosTransacao = Math.min(template.pontos, pontosRestantesGanho);
    
    const data = new Date(hoje);
    data.setDate(data.getDate() - diaAtual);
    
    const saldoAnterior = saldo - pontosTransacao;
    
    transacoes.push({
      id: id++,
      data: data.toISOString(),
      tipo: 'ganho',
      descricao: template.descricao,
      pontos: pontosTransacao,
      xp: template.xp,
      saldoAnterior,
      saldoAtual: saldo,
      detalhesPremiacao: template.detalhesPremiacao
    });
    
    saldo = saldoAnterior;
    pontosRestantesGanho -= pontosTransacao;
    diaAtual += Math.floor(Math.random() * 3) + 1;
  }

  // Adicionar alguns bônus
  const numBonus = Math.min(3, Math.floor(pontosGanhos / 2000));
  for (let i = 0; i < numBonus; i++) {
    const template = templatesBonus[i % templatesBonus.length];
    const data = new Date(hoje);
    data.setDate(data.getDate() - (15 + i * 20));
    
    const saldoAnterior = saldo - template.pontos;
    
    transacoes.push({
      id: id++,
      data: data.toISOString(),
      tipo: 'bonus',
      descricao: template.descricao,
      pontos: template.pontos,
      saldoAnterior,
      saldoAtual: saldo,
      detalhesPremiacao: template.detalhesPremiacao
    });
    
    saldo = saldoAnterior;
  }

  // Gerar transações de resgate
  diaAtual = 5;
  while (pontosRestantesGasto > 0 && diaAtual < 85) {
    const template = templatesResgate[Math.floor(Math.random() * templatesResgate.length)];
    const pontosTransacao = Math.min(template.pontos, pontosRestantesGasto);
    
    const data = new Date(hoje);
    data.setDate(data.getDate() - diaAtual);
    
    const saldoAnterior = saldo + pontosTransacao;
    
    transacoes.push({
      id: id++,
      data: data.toISOString(),
      tipo: 'resgate',
      descricao: template.descricao,
      pontos: -pontosTransacao,
      saldoAnterior,
      saldoAtual: saldo
    });
    
    saldo = saldoAnterior;
    pontosRestantesGasto -= pontosTransacao;
    diaAtual += Math.floor(Math.random() * 10) + 5;
  }

  // Adicionar alguns ajustes
  if (pontosGanhos > 3000) {
    const numAjustes = Math.floor(Math.random() * 2) + 1;
    for (let i = 0; i < numAjustes; i++) {
      const template = templatesAjuste[i % templatesAjuste.length];
      const data = new Date(hoje);
      data.setDate(data.getDate() - (30 + i * 15));
      
      const saldoAnterior = saldo - template.pontos;
      
      transacoes.push({
        id: id++,
        data: data.toISOString(),
        tipo: 'ajuste',
        descricao: template.descricao,
        pontos: template.pontos,
        saldoAnterior,
        saldoAtual: saldo
      });
      
      saldo = saldoAnterior;
    }
  }

  // Adicionar expirações se houver muitos pontos gastos
  if (pontosGastos > 5000) {
    const template = templatesExpiracao[0];
    const data = new Date(hoje);
    data.setDate(data.getDate() - 60);
    
    const saldoAnterior = saldo + template.pontos;
    
    transacoes.push({
      id: id++,
      data: data.toISOString(),
      tipo: 'expiracao',
      descricao: template.descricao,
      pontos: -template.pontos,
      saldoAnterior,
      saldoAtual: saldo
    });
  }

  // Ordenar por data (mais recente primeiro)
  return transacoes.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
};

const ITENS_POR_PAGINA_OPCOES = [10, 20, 50];

export default function ExtratoPage() {
  const { campanhaAtual } = useParticipante();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filtroTipo, setFiltroTipo] = useState<TipoFiltro>('todos');
  const [periodoSelecionado, setPeriodoSelecionado] = useState('90');
  const [showGrafico, setShowGrafico] = useState(false);
  const [showComparacao, setShowComparacao] = useState(false);
  const [detalhesExpandidos, setDetalhesExpandidos] = useState<Set<number>>(new Set());
  const [busca, setBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  // Lê parâmetros da URL ao entrar na página (vindo de recompensas)
  useEffect(() => {
    const buscaParam = searchParams.get('busca');
    const tipoParam = searchParams.get('tipo') as TipoFiltro | null;
    if (buscaParam) {
      setBusca(buscaParam);
    }
    if (tipoParam && ['ganho', 'resgate', 'bonus', 'ajuste', 'expiracao', 'todos'].includes(tipoParam)) {
      setFiltroTipo(tipoParam);
    }
  }, [searchParams]);

  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroTipo, periodoSelecionado, busca, itensPorPagina]);

  const toggleDetalhes = (id: number) => {
    setDetalhesExpandidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatarData = (dataStr: string) => {
    const data = new Date(dataStr);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!campanhaAtual) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <i className="ri-inbox-line text-6xl text-slate-300 mb-4"></i>
          <p className="text-slate-500">Nenhuma campanha selecionada</p>
        </div>
      </div>
    );
  }

  const transacoes = gerarTransacoes(
    campanhaAtual.id,
    campanhaAtual.progressao.pontosDisponiveis,
    campanhaAtual.pontosGanhos,
    campanhaAtual.pontosDistribuidos
  );

  // Filtrar transações
  const transacoesFiltradas = transacoes.filter(t => {
    if (filtroTipo !== 'todos' && t.tipo !== filtroTipo) return false;

    const diasAtras = parseInt(periodoSelecionado);
    const dataLimite = new Date();
    dataLimite.setDate(dataLimite.getDate() - diasAtras);
    if (new Date(t.data) < dataLimite) return false;

    if (busca.trim()) {
      const termo = busca.toLowerCase();
      const dataFormatada = formatarData(t.data).toLowerCase();
      const tipoLabel = (tipoConfig[t.tipo as keyof typeof tipoConfig]?.label ?? 'Expiração').toLowerCase();
      const pontos = t.pontos.toString();
      const xp = t.xp ? t.xp.toString() : '';

      const matchBasico =
        t.descricao.toLowerCase().includes(termo) ||
        dataFormatada.includes(termo) ||
        tipoLabel.includes(termo) ||
        pontos.includes(termo) ||
        xp.includes(termo);

      if (matchBasico) return true;

      // Busca dentro dos detalhes de premiação
      if (t.detalhesPremiacao) {
        const dp = t.detalhesPremiacao;
        if (dp.meta?.toLowerCase().includes(termo)) return true;
        if (dp.observacoes?.toLowerCase().includes(termo)) return true;
        if (dp.contrato?.toLowerCase().includes(termo)) return true;
        if (dp.clienteOS?.toLowerCase().includes(termo)) return true;
        if (dp.numeroOS?.toLowerCase().includes(termo)) return true;
        if (dp.produtos?.some(p => p.toLowerCase().includes(termo))) return true;
        if (dp.servicos?.some(s => s.toLowerCase().includes(termo))) return true;
        if (dp.lotesPremiacao?.some(lote =>
          lote.metaAtingida.toLowerCase().includes(termo) ||
          lote.contrato.toLowerCase().includes(termo) ||
          lote.cliente.toLowerCase().includes(termo) ||
          lote.observacao?.toLowerCase().includes(termo) ||
          lote.produtosVendidos.some(p =>
            p.produto.toLowerCase().includes(termo) ||
            p.informacao.toLowerCase().includes(termo)
          )
        )) return true;
      }

      return false;
    }

    return true;
  });

  // Paginação
  const totalPaginas = Math.max(1, Math.ceil(transacoesFiltradas.length / itensPorPagina));
  const indiceInicio = (paginaAtual - 1) * itensPorPagina;
  const indiceFim = indiceInicio + itensPorPagina;
  const transacoesPagina = transacoesFiltradas.slice(indiceInicio, indiceFim);

  const irParaPagina = (pagina: number) => {
    const p = Math.max(1, Math.min(pagina, totalPaginas));
    setPaginaAtual(p);
    // Scroll suave até o topo da lista
    document.getElementById('lista-transacoes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getPaginasVisiveis = () => {
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, paginaAtual - delta); i <= Math.min(totalPaginas, paginaAtual + delta); i++) {
      range.push(i);
    }
    return range;
  };

  // Calcular resgates PIX acumulados no mês atual
  const calcularResgatesPIXMensal = () => {
    const hoje = new Date();
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    
    const resgatesPIX = transacoes.filter(t => 
      t.tipo === 'resgate' && 
      t.descricao.includes('iCash (PIX)') &&
      new Date(t.data) >= inicioMes
    );

    const totalAcumulado = Math.abs(resgatesPIX.reduce((sum, t) => sum + t.pontos, 0));
    
    return {
      total: totalAcumulado,
      quantidade: resgatesPIX.length,
      transacoes: resgatesPIX
    };
  };

  const resgatesPIX = calcularResgatesPIXMensal();

  // Determinar nível de atenção (0 a 5.000 pontos)
  const getNivelAtencao = (total: number) => {
    const percentual = Math.min((total / 5000) * 100, 100);
    
    if (percentual === 0) return null;
    
    let cor = 'blue';
    let icone = 'ri-information-line';
    let titulo = 'Informação';
    let intensidade = 'baixa';
    
    if (percentual >= 80) {
      cor = 'red';
      icone = 'ri-alert-fill';
      titulo = 'Atenção Crítica';
      intensidade = 'critica';
    } else if (percentual >= 60) {
      cor = 'orange';
      icone = 'ri-error-warning-fill';
      titulo = 'Atenção Alta';
      intensidade = 'alta';
    } else if (percentual >= 40) {
      cor = 'amber';
      icone = 'ri-alert-line';
      titulo = 'Atenção Moderada';
      intensidade = 'moderada';
    } else if (percentual >= 20) {
      cor = 'yellow';
      icone = 'ri-information-fill';
      titulo = 'Atenção Baixa';
      intensidade = 'baixa';
    }
    
    return { percentual, cor, icone, titulo, intensidade };
  };

  const nivelAtencao = getNivelAtencao(resgatesPIX.total);

  // Calcular estatísticas do período filtrado
  const totalGanho = transacoesFiltradas
    .filter(t => t.pontos > 0)
    .reduce((sum, t) => sum + t.pontos, 0);
  
  const totalGasto = Math.abs(
    transacoesFiltradas
      .filter(t => t.pontos < 0)
      .reduce((sum, t) => sum + t.pontos, 0)
  );

  const totalXP = transacoesFiltradas
    .filter(t => t.xp && t.xp > 0)
    .reduce((sum, t) => sum + (t.xp || 0), 0);

  // Função para exportar para PDF via window.print()
  const exportarPDF = () => {
    const dataEmissao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const linhasTabela = transacoesFiltradas.map(t => `
      <tr>
        <td>${formatarData(t.data)}</td>
        <td>${tipoConfig[t.tipo as keyof typeof tipoConfig]?.label ?? 'Expiração'}</td>
        <td>${t.descricao}</td>
        <td class="${t.pontos > 0 ? 'positivo' : 'negativo'}">${t.pontos > 0 ? '+' : ''}${t.pontos.toLocaleString('pt-BR')}</td>
        <td>${t.xp ? `+${t.xp}` : '-'}</td>
        <td>${t.saldoAtual.toLocaleString('pt-BR')}</td>
      </tr>
    `).join('');

    const estilos = `
      <style>
        @media print { body { margin: 0; } }
        body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
        h1 { color: #0f172a; border-bottom: 3px solid #f97316; padding-bottom: 10px; margin-bottom: 6px; }
        .meta { color: #64748b; font-size: 13px; margin-bottom: 20px; }
        .info { background: #f1f5f9; padding: 14px 18px; border-radius: 8px; margin-bottom: 20px; display: flex; gap: 40px; flex-wrap: wrap; }
        .info-item .label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; }
        .info-item .value { font-size: 18px; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f97316; color: white; padding: 10px 12px; text-align: left; }
        td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background: #f8fafc; }
        .positivo { color: #10b981; font-weight: 700; }
        .negativo { color: #ef4444; font-weight: 700; }
        .footer { margin-top: 28px; text-align: center; color: #94a3b8; font-size: 11px; border-top: 1px solid #e2e8f0; padding-top: 14px; }
      </style>
    `;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Extrato de Pontos</title>${estilos}</head><body>
      <h1>Extrato de Pontos</h1>
      <div class="meta">
        <strong>Campanha:</strong> ${campanhaAtual?.nome} &nbsp;|&nbsp;
        <strong>Período:</strong> Últimos ${periodoSelecionado} dias &nbsp;|&nbsp;
        <strong>Emissão:</strong> ${dataEmissao}
      </div>
      <div class="info">
        <div class="info-item"><div class="label">Saldo Atual</div><div class="value">${campanhaAtual?.progressao.pontosDisponiveis.toLocaleString('pt-BR')} pts</div></div>
        <div class="info-item"><div class="label">Total Ganho</div><div class="value positivo">+${totalGanho.toLocaleString('pt-BR')} pts</div></div>
        <div class="info-item"><div class="label">Total Gasto</div><div class="value negativo">-${totalGasto.toLocaleString('pt-BR')} pts</div></div>
        <div class="info-item"><div class="label">XP Ganho</div><div class="value">+${totalXP.toLocaleString('pt-BR')} XP</div></div>
      </div>
      <table>
        <thead><tr><th>Data</th><th>Tipo</th><th>Descrição</th><th>Pontos</th><th>XP</th><th>Saldo</th></tr></thead>
        <tbody>${linhasTabela}</tbody>
      </table>
      <div class="footer">
        <p>Documento gerado automaticamente pelo sistema de incentivos. Para mais informações, acesse sua conta na plataforma.</p>
      </div>
    </body></html>`;

    // Injeta iframe oculto e dispara window.print() sem abrir nova aba
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 300);
    }
  };

  // Função para exportar para Excel (CSV)
  const exportarExcel = () => {
    // Criar conteúdo CSV
    let csv = 'Data,Tipo,Descrição,Pontos,XP,Saldo Anterior,Saldo Atual\n';
    
    transacoesFiltradas.forEach(t => {
      csv += `"${formatarData(t.data)}","${tipoConfig[t.tipo as keyof typeof tipoConfig]?.label ?? 'Expiração'}","${t.descricao}",${t.pontos},${t.xp || 0},${t.saldoAnterior},${t.saldoAtual}\n`;
    });

    // Adicionar resumo
    csv += '\n\nResumo do Período\n';
    csv += `Campanha,"${campanhaAtual?.nome}"\n`;
    csv += `Período,Últimos ${periodoSelecionado} dias\n`;
    csv += `Saldo Atual,${campanhaAtual?.progressao.pontosDisponiveis}\n`;
    csv += `Total Ganho,${totalGanho}\n`;
    csv += `Total Gasto,${totalGasto}\n`;
    csv += `XP Ganho,${totalXP}\n`;

    // Criar blob e download
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `extrato_pontos_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dados para gráfico de evolução
  const dadosGrafico = () => {
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const dados = meses.map((mes, index) => {
      const ganhos = transacoesFiltradas
        .filter(t => t.pontos > 0 && new Date(t.data).getMonth() === index)
        .reduce((sum, t) => sum + t.pontos, 0);
      
      return { mes, pontos: ganhos };
    });
    
    return dados;
  };

  const maxPontos = Math.max(...dadosGrafico().map(d => d.pontos), 1);

  // Comparação mensal
  const getComparacaoMensal = () => {
    const mesAtual = new Date().getMonth();
    const mesAnterior = mesAtual - 1;

    const pontosAtual = transacoesFiltradas
      .filter(t => t.pontos > 0 && new Date(t.data).getMonth() === mesAtual)
      .reduce((sum, t) => sum + t.pontos, 0);

    const pontosAnterior = transacoesFiltradas
      .filter(t => t.pontos > 0 && new Date(t.data).getMonth() === mesAnterior)
      .reduce((sum, t) => sum + t.pontos, 0);

    const variacao = pontosAnterior > 0 
      ? ((pontosAtual - pontosAnterior) / pontosAnterior) * 100 
      : 0;

    return { pontosAtual, pontosAnterior, variacao };
  };

  const comparacao = getComparacaoMensal();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Relatório de Pontos</h1>
        <p className="text-slate-600">
          Acompanhe todas as suas transações de pontos e XP da campanha <strong>{campanhaAtual.nome}</strong>
        </p>
      </div>

      {/* Alerta de Resgates PIX Acumulados */}
      {nivelAtencao && (
        <div className={`bg-gradient-to-br ${
          nivelAtencao.cor === 'red' ? 'from-red-50 to-rose-50 border-red-200' :
          nivelAtencao.cor === 'orange' ? 'from-orange-50 to-amber-50 border-orange-200' :
          nivelAtencao.cor === 'amber' ? 'from-amber-50 to-yellow-50 border-amber-200' :
          nivelAtencao.cor === 'yellow' ? 'from-yellow-50 to-lime-50 border-yellow-200' :
          'from-blue-50 to-cyan-50 border-blue-200'
        } rounded-xl p-6 border-2 shadow-sm`}>
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl ${
              nivelAtencao.cor === 'red' ? 'bg-red-500' :
              nivelAtencao.cor === 'orange' ? 'bg-orange-500' :
              nivelAtencao.cor === 'amber' ? 'bg-amber-500' :
              nivelAtencao.cor === 'yellow' ? 'bg-yellow-500' :
              'bg-blue-500'
            } flex items-center justify-center flex-shrink-0 ${
              nivelAtencao.intensidade === 'critica' ? 'animate-pulse' : ''
            }`}>
              <i className={`${nivelAtencao.icone} text-2xl text-white`}></i>
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className={`text-lg font-bold mb-1 ${
                    nivelAtencao.cor === 'red' ? 'text-red-900' :
                    nivelAtencao.cor === 'orange' ? 'text-orange-900' :
                    nivelAtencao.cor === 'amber' ? 'text-amber-900' :
                    nivelAtencao.cor === 'yellow' ? 'text-yellow-900' :
                    'text-blue-900'
                  }`}>
                    {nivelAtencao.titulo}: Resgates PIX no Mês
                  </h3>
                  <p className={`text-sm ${
                    nivelAtencao.cor === 'red' ? 'text-red-700' :
                    nivelAtencao.cor === 'orange' ? 'text-orange-700' :
                    nivelAtencao.cor === 'amber' ? 'text-amber-700' :
                    nivelAtencao.cor === 'yellow' ? 'text-yellow-700' :
                    'text-blue-700'
                  }`}>
                    Você acumulou <strong>{resgatesPIX.total.toLocaleString('pt-BR')} pontos</strong> em resgates iCash (PIX) neste mês
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold ${
                    nivelAtencao.cor === 'red' ? 'text-red-600' :
                    nivelAtencao.cor === 'orange' ? 'text-orange-600' :
                    nivelAtencao.cor === 'amber' ? 'text-amber-600' :
                    nivelAtencao.cor === 'yellow' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`}>
                    {resgatesPIX.total.toLocaleString('pt-BR')}
                  </div>
                  <div className="text-sm text-slate-600 font-medium">
                    de 5.000 pontos
                  </div>
                </div>
              </div>

              {/* Barra de Progresso */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs font-semibold mb-2">
                  <span className={`${
                    nivelAtencao.cor === 'red' ? 'text-red-700' :
                    nivelAtencao.cor === 'orange' ? 'text-orange-700' :
                    nivelAtencao.cor === 'amber' ? 'text-amber-700' :
                    nivelAtencao.cor === 'yellow' ? 'text-yellow-700' :
                    'text-blue-700'
                  }`}>
                    Progresso: {nivelAtencao.percentual.toFixed(1)}%
                  </span>
                  <span className="text-slate-600">
                    {resgatesPIX.quantidade} {resgatesPIX.quantidade === 1 ? 'resgate' : 'resgates'}
                  </span>
                </div>
                <div className="relative h-4 bg-white rounded-full overflow-hidden border-2 border-slate-200">
                  <div 
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
                      nivelAtencao.cor === 'red' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                      nivelAtencao.cor === 'orange' ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
                      nivelAtencao.cor === 'amber' ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                      nivelAtencao.cor === 'yellow' ? 'bg-gradient-to-r from-yellow-500 to-lime-500' :
                      'bg-gradient-to-r from-blue-500 to-cyan-500'
                    }`}
                    style={{ width: `${nivelAtencao.percentual}%` }}
                  >
                    {nivelAtencao.percentual > 10 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-white drop-shadow">
                          {nivelAtencao.percentual.toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Mensagem de Orientação */}
              <div className={`flex items-start gap-2 p-3 rounded-lg ${
                nivelAtencao.cor === 'red' ? 'bg-red-100 border border-red-200' :
                nivelAtencao.cor === 'orange' ? 'bg-orange-100 border border-orange-200' :
                nivelAtencao.cor === 'amber' ? 'bg-amber-100 border border-amber-200' :
                nivelAtencao.cor === 'yellow' ? 'bg-yellow-100 border border-yellow-200' :
                'bg-blue-100 border border-blue-200'
              }`}>
                <i className={`${
                  nivelAtencao.cor === 'red' ? 'ri-shield-cross-fill text-red-600' :
                  nivelAtencao.cor === 'orange' ? 'ri-shield-star-fill text-orange-600' :
                  nivelAtencao.cor === 'amber' ? 'ri-shield-check-fill text-amber-600' :
                  nivelAtencao.cor === 'yellow' ? 'ri-shield-line text-yellow-600' :
                  'ri-information-line text-blue-600'
                } text-lg mt-0.5 flex-shrink-0`}></i>
                <p className={`text-sm ${
                  nivelAtencao.cor === 'red' ? 'text-red-800' :
                  nivelAtencao.cor === 'orange' ? 'text-orange-800' :
                  nivelAtencao.cor === 'amber' ? 'text-amber-800' :
                  nivelAtencao.cor === 'yellow' ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>
                  {nivelAtencao.intensidade === 'critica' && (
                    <span><strong>Limite quase atingido!</strong> Você está próximo do limite mensal de 5.000 pontos em resgates PIX. Considere outras opções de resgate disponíveis.</span>
                  )}
                  {nivelAtencao.intensidade === 'alta' && (
                    <span><strong>Atenção ao limite!</strong> Você já utilizou mais de 60% do limite mensal de resgates PIX. Planeje seus próximos resgates com cuidado.</span>
                  )}
                  {nivelAtencao.intensidade === 'moderada' && (
                    <span><strong>Acompanhe seus resgates:</strong> Você está utilizando uma quantidade moderada do limite mensal. Continue monitorando para não ultrapassar 5.000 pontos.</span>
                  )}
                  {nivelAtencao.intensidade === 'baixa' && (
                    <span><strong>Tudo sob controle:</strong> Seus resgates PIX estão dentro do esperado. Você ainda tem boa margem disponível neste mês.</span>
                  )}
                </p>
              </div>

              {/* Detalhes dos Resgates */}
              {resgatesPIX.quantidade > 0 && (
                <details className="mt-3">
                  <summary className={`text-sm font-semibold cursor-pointer hover:underline ${
                    nivelAtencao.cor === 'red' ? 'text-red-700' :
                    nivelAtencao.cor === 'orange' ? 'text-orange-700' :
                    nivelAtencao.cor === 'amber' ? 'text-amber-700' :
                    nivelAtencao.cor === 'yellow' ? 'text-yellow-700' :
                    'text-blue-700'
                  }`}>
                    Ver detalhes dos {resgatesPIX.quantidade} {resgatesPIX.quantidade === 1 ? 'resgate' : 'resgates'}
                  </summary>
                  <div className="mt-3 space-y-2">
                    {resgatesPIX.transacoes.map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-exchange-dollar-line text-slate-600"></i>
                          <span className="text-sm text-slate-700">
                            {formatarData(t.data)}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-rose-600">
                          {t.pontos.toLocaleString('pt-BR')} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Saldo Atual</span>
            <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
              <i className="ri-wallet-3-fill text-lg text-teal-600"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {campanhaAtual.progressao.pontosDisponiveis.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-slate-500 mt-1">pontos disponíveis</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Total Ganho</span>
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <i className="ri-arrow-up-circle-fill text-lg text-emerald-600"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600">
            +{totalGanho.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-slate-500 mt-1">no período</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Total Gasto</span>
            <div className="w-10 h-10 rounded-lg bg-rose-500 flex items-center justify-center">
              <i className="ri-arrow-down-circle-fill text-lg text-rose-600"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-rose-600">
            -{totalGasto.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-slate-500 mt-1">no período</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">XP Ganho</span>
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
              <i className="ri-star-fill text-lg text-amber-600"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            +{totalXP.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-slate-500 mt-1">no período</p>
        </div>
      </div>

      {/* Resumo da Campanha */}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <i className="ri-bar-chart-box-fill text-2xl text-teal-600"></i>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-3">
              Resumo Geral da Campanha
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total de Pontos Ganhos</p>
                <p className="text-xl font-bold text-emerald-600">
                  +{campanhaAtual.pontosGanhos.toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Total de Pontos Gastos</p>
                <p className="text-xl font-bold text-rose-600">
                  -{(campanhaAtual.pontosDistribuidos - campanhaAtual.progressao.pontosDisponiveis).toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Pontos Disponíveis</p>
                <p className="text-xl font-bold text-teal-600">
                  {campanhaAtual.progressao.pontosDisponiveis.toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Filtro por Tipo */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tipo de Transação
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFiltroTipo('todos')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  filtroTipo === 'todos'
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Todos
              </button>
              {Object.entries(tipoConfig).map(([tipo, config]) => (
                <button
                  key={tipo}
                  onClick={() => setFiltroTipo(tipo as TipoTransacao)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    filtroTipo === tipo
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <i className={`${config.icon} mr-1`}></i>
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filtro por Período */}
          <div className="w-full md:w-48">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Período
            </label>
            <select
              value={periodoSelecionado}
              onChange={(e) => setPeriodoSelecionado(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="180">Últimos 6 meses</option>
              <option value="365">Último ano</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Transações */}
      <div id="lista-transacoes" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center justify-between flex-1">
              <h2 className="text-lg font-semibold text-slate-900">
                Histórico de Transações
              </h2>
              <span className="text-sm text-slate-500 md:hidden">
                {transacoesFiltradas.length} transações
              </span>
            </div>
            <div className="flex items-center gap-3 flex-1 md:max-w-sm">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-slate-400 text-base"></i>
                </div>
                <input
                  type="text"
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  placeholder="Buscar por descrição, data, contrato..."
                  className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                {busca && (
                  <button
                    onClick={() => setBusca('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                )}
              </div>
              <span className="text-sm text-slate-500 whitespace-nowrap hidden md:block">
                {transacoesFiltradas.length} transações
              </span>
            </div>
          </div>
          {busca && (
            <p className="mt-2 text-xs text-teal-700 font-medium">
              <i className="ri-filter-3-line mr-1"></i>
              Mostrando resultados para &quot;{busca}&quot;
            </p>
          )}
        </div>

        {transacoesFiltradas.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-inbox-line text-6xl text-slate-300 mb-4"></i>
            <p className="text-slate-500">Nenhuma transação encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {transacoesPagina.map((transacao) => {
              const config = tipoConfig[transacao.tipo as keyof typeof tipoConfig] ?? {
                label: 'Expiração',
                icon: 'ri-time-fill',
                color: 'text-amber-600',
                bgColor: 'bg-amber-50',
                borderColor: 'border-l-amber-500',
                rowBg: 'bg-amber-50/30',
                rowHover: 'hover:bg-amber-50/60'
              };
              return (
                <div
                  key={transacao.id}
                  className={`p-6 border-l-4 ${config.borderColor} ${config.rowBg} ${config.rowHover} transition-colors`}
                >
                  <div className="flex items-start gap-4">
                    {/* Ícone */}
                    <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${config.icon} text-xl ${config.color}`}></i>
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-1">
                        <div>
                          <h3 className="font-medium text-slate-900 mb-1">
                            {transacao.descricao}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {formatarData(transacao.data)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-bold ${
                            transacao.pontos > 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {transacao.pontos > 0 ? '+' : ''}{transacao.pontos.toLocaleString('pt-BR')}
                          </p>
                          {transacao.xp && transacao.xp > 0 && (
                            <p className="text-sm text-amber-600 font-medium">
                              +{transacao.xp} XP
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Detalhes da Premiação - Apenas para créditos (ganho e bonus) */}
                      {transacao.detalhesPremiacao && (transacao.tipo === 'ganho' || transacao.tipo === 'bonus') && (
                        <div className="mt-3">
                          <button
                            onClick={() => toggleDetalhes(transacao.id)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                              detalhesExpandidos.has(transacao.id)
                                ? 'bg-teal-100 text-teal-800 hover:bg-teal-200'
                                : 'bg-teal-600 text-white hover:bg-teal-700'
                            }`}
                          >
                            <i className={`${detalhesExpandidos.has(transacao.id) ? 'ri-eye-off-line' : 'ri-trophy-line'} text-base`}></i>
                            {detalhesExpandidos.has(transacao.id) ? 'Ocultar detalhes' : 'Ver detalhes da premiação'}
                            <i className={detalhesExpandidos.has(transacao.id) ? 'ri-arrow-up-s-line text-base' : 'ri-arrow-down-s-line text-base'}></i>
                          </button>
                        </div>
                      )}
                      {transacao.detalhesPremiacao && (transacao.tipo === 'ganho' || transacao.tipo === 'bonus') && detalhesExpandidos.has(transacao.id) && (
                        <div className="mt-2 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                              <i className="ri-trophy-fill text-emerald-600"></i>
                            </div>
                            <div className="flex-1 space-y-2">
                              <h4 className="font-semibold text-slate-900 text-sm">
                                Detalhes da Premiação
                              </h4>

                              {/* Renderização de Lotes de Premiação (Meta de Vendas) */}
                              {transacao.detalhesPremiacao.lotesPremiacao && transacao.detalhesPremiacao.lotesPremiacao.length > 0 && (
                                <div className="space-y-6">
                                  {transacao.detalhesPremiacao.lotesPremiacao.map((lote, loteIdx) => {
                                    const totalGeral = lote.produtosVendidos.reduce((sum, p) => sum + p.total, 0);
                                    const descontoGeral = lote.produtosVendidos.reduce((sum, p) => sum + p.desconto, 0);
                                    const liquidoGeral = lote.produtosVendidos.reduce((sum, p) => sum + p.liquido, 0);
                                    
                                    return (
                                      <div key={loteIdx} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                        {/* Header do Lote */}
                                        <div className="p-4 border-b border-slate-100">
                                          <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center flex-shrink-0">
                                              <i className="ri-arrow-down-s-fill text-white text-lg"></i>
                                            </div>
                                            <div className="flex-1">
                                              <h5 className="font-semibold text-slate-900 text-sm mb-2">Detalhes da Premiação</h5>
                                              
                                              {/* Meta Atingida */}
                                              <div className="flex items-start gap-2 mb-3">
                                                <i className="ri-flag-fill text-emerald-600 text-sm mt-0.5"></i>
                                                <div>
                                                  <p className="text-xs text-slate-500 font-medium">Meta Atingida:</p>
                                                  <p className="text-sm text-slate-900">{lote.metaAtingida}</p>
                                                </div>
                                              </div>

                                              {/* Informações do Contrato */}
                                              <div className="flex flex-wrap items-center gap-4 p-3 bg-slate-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                  <div className="w-6 h-6 rounded bg-indigo-100 flex items-center justify-center">
                                                    <i className="ri-file-text-fill text-indigo-600 text-xs"></i>
                                                  </div>
                                                  <div>
                                                    <p className="text-xs text-slate-500">Contrato</p>
                                                    <p className="text-xs font-bold text-indigo-600">{lote.contrato}</p>
                                                  </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-2">
                                                  <div className="w-6 h-6 rounded bg-violet-100 flex items-center justify-center">
                                                    <i className="ri-user-fill text-violet-600 text-xs"></i>
                                                  </div>
                                                  <div>
                                                    <p className="text-xs text-slate-500">Cliente</p>
                                                    <p className="text-xs font-bold text-violet-600">{lote.cliente}</p>
                                                  </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                  <div className="w-6 h-6 rounded bg-rose-100 flex items-center justify-center">
                                                    <i className="ri-calendar-fill text-rose-600 text-xs"></i>
                                                  </div>
                                                  <div>
                                                    <p className="text-xs text-slate-500">Data</p>
                                                    <p className="text-xs font-bold text-rose-600">{lote.data}</p>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Produtos */}
                                        <div className="p-4">
                                          <div className="flex items-center gap-2 mb-3">
                                            <i className="ri-shopping-bag-fill text-teal-600"></i>
                                            <span className="text-sm font-medium text-slate-700">Produtos:</span>
                                          </div>
                                          
                                          <div className="flex flex-wrap gap-3 mb-4">
                                            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-200">
                                              <i className="ri-money-dollar-circle-fill text-emerald-600"></i>
                                              <div>
                                                <p className="text-xs text-slate-500">Valor Total</p>
                                                <p className="text-sm font-bold text-emerald-600">R$ {lote.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg border border-teal-200">
                                              <i className="ri-calendar-check-fill text-teal-600"></i>
                                              <div>
                                                <p className="text-xs text-slate-500">Período</p>
                                                <p className="text-sm font-bold text-teal-600">{lote.periodo}</p>
                                              </div>
                                            </div>
                                          </div>

                                          {lote.observacao && (
                                            <div className="flex items-start gap-2 mb-4 p-2 bg-slate-50 rounded-lg">
                                              <i className="ri-information-fill text-slate-500 text-sm mt-0.5"></i>
                                              <p className="text-xs text-slate-600">{lote.observacao}</p>
                                            </div>
                                          )}

                                          {/* Tabela de Discriminação de Produtos Vendidos */}
                                          <div className="mt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                              <i className="ri-list-check text-slate-700"></i>
                                              <span className="text-sm font-semibold text-slate-800">Discriminação dos Produtos Vendidos</span>
                                            </div>
                                            
                                            <div className="overflow-x-auto rounded-lg border border-slate-200">
                                              <table className="w-full text-xs">
                                                <thead>
                                                  <tr className="bg-emerald-500 text-white">
                                                    <th className="px-3 py-2 text-left font-semibold">Produto</th>
                                                    <th className="px-3 py-2 text-center font-semibold">Qtd</th>
                                                    <th className="px-3 py-2 text-right font-semibold">Valor Unit.</th>
                                                    <th className="px-3 py-2 text-right font-semibold">Total</th>
                                                    <th className="px-3 py-2 text-right font-semibold">Desconto</th>
                                                    <th className="px-3 py-2 text-right font-semibold">Líquido</th>
                                                    <th className="px-3 py-2 text-left font-semibold">Informação</th>
                                                  </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                  {lote.produtosVendidos.map((produto, prodIdx) => (
                                                    <tr key={prodIdx} className="hover:bg-slate-50">
                                                      <td className="px-3 py-2 font-medium text-slate-900">{produto.produto}</td>
                                                      <td className="px-3 py-2 text-center text-slate-700">{produto.qtd}</td>
                                                      <td className="px-3 py-2 text-right text-slate-700">R$ {produto.valorUnit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                      <td className="px-3 py-2 text-right font-medium text-teal-600">R$ {produto.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                      <td className="px-3 py-2 text-right text-rose-600">R$ {produto.desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                      <td className="px-3 py-2 text-right font-bold text-emerald-600">R$ {produto.liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                      <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={produto.informacao}>{produto.informacao}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                                <tfoot>
                                                  <tr className="bg-teal-500 text-white font-semibold">
                                                    <td className="px-3 py-2 text-right" colSpan={3}>TOTAL GERAL:</td>
                                                    <td className="px-3 py-2 text-right">R$ {totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                    <td className="px-3 py-2 text-right">R$ {descontoGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                    <td className="px-3 py-2 text-right">R$ {liquidoGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                    <td className="px-3 py-2"></td>
                                                  </tr>
                                                </tfoot>
                                              </table>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {transacao.detalhesPremiacao.meta && !transacao.detalhesPremiacao.lotesPremiacao && (
                                <div className="flex items-start gap-2">
                                  <i className="ri-flag-fill text-emerald-600 text-sm mt-0.5"></i>
                                  <div>
                                    <p className="text-xs text-slate-600 font-medium">Meta Atingida:</p>
                                    <p className="text-sm text-slate-900">{transacao.detalhesPremiacao.meta}</p>
                                  </div>
                                </div>
                              )}

                              {/* Informações de Contrato e OS */}
                              {(transacao.detalhesPremiacao.contrato || transacao.detalhesPremiacao.numeroOS) && !transacao.detalhesPremiacao.lotesPremiacao && (
                                <div className="flex flex-wrap gap-3 p-3 bg-white rounded-lg border border-teal-100">
                                  {transacao.detalhesPremiacao.contrato && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                                        <i className="ri-file-text-fill text-indigo-600 text-sm"></i>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Contrato</p>
                                        <p className="text-sm font-bold text-indigo-600">{transacao.detalhesPremiacao.contrato}</p>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {transacao.detalhesPremiacao.numeroOS && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center">
                                        <i className="ri-file-list-3-fill text-cyan-600 text-sm"></i>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Ordem de Serviço</p>
                                        <p className="text-sm font-bold text-cyan-600">{transacao.detalhesPremiacao.numeroOS}</p>
                                      </div>
                                    </div>
                                  )}

                                  {transacao.detalhesPremiacao.clienteOS && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                                        <i className="ri-building-fill text-violet-600 text-sm"></i>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Cliente</p>
                                        <p className="text-sm font-bold text-violet-600">{transacao.detalhesPremiacao.clienteOS}</p>
                                      </div>
                                    </div>
                                  )}

                                  {transacao.detalhesPremiacao.dataOS && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                                        <i className="ri-calendar-event-fill text-rose-600 text-sm"></i>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Data OS</p>
                                        <p className="text-sm font-bold text-rose-600">{transacao.detalhesPremiacao.dataOS}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {transacao.detalhesPremiacao.produtos && transacao.detalhesPremiacao.produtos.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <i className="ri-shopping-bag-fill text-teal-600 text-sm mt-0.5"></i>
                                  <div>
                                    <p className="text-xs text-slate-600 font-medium">Produtos Vendidos:</p>
                                    <ul className="text-sm text-slate-900 space-y-0.5">
                                      {transacao.detalhesPremiacao.produtos.map((produto, idx) => (
                                        <li key={idx} className="flex items-center gap-1">
                                          <span className="w-1 h-1 rounded-full bg-teal-600"></span>
                                          {produto}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}

                              {transacao.detalhesPremiacao.servicos && transacao.detalhesPremiacao.servicos.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <i className="ri-customer-service-fill text-blue-600 text-sm mt-0.5"></i>
                                  <div>
                                    <p className="text-xs text-slate-600 font-medium">Serviços Realizados:</p>
                                    <ul className="text-sm text-slate-900 space-y-0.5">
                                      {transacao.detalhesPremiacao.servicos.map((servico, idx) => (
                                        <li key={idx} className="flex items-center gap-1">
                                          <span className="w-1 h-1 rounded-full bg-blue-600"></span>
                                          {servico}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}

                              <div className="flex flex-wrap gap-3 pt-2">
                                {transacao.detalhesPremiacao.valorVendido && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg">
                                    <i className="ri-money-dollar-circle-fill text-emerald-600"></i>
                                    <div>
                                      <p className="text-xs text-slate-600">Valor Total</p>
                                      <p className="text-sm font-bold text-emerald-600">
                                        R$ {transacao.detalhesPremiacao.valorVendido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {transacao.detalhesPremiacao.quantidadeAtendimentos && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg">
                                    <i className="ri-user-star-fill text-blue-600"></i>
                                    <div>
                                      <p className="text-xs text-slate-600">Atendimentos</p>
                                      <p className="text-sm font-bold text-blue-600">
                                        {transacao.detalhesPremiacao.quantidadeAtendimentos}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {transacao.detalhesPremiacao.periodo && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg">
                                    <i className="ri-calendar-check-fill text-purple-600"></i>
                                    <div>
                                      <p className="text-xs text-slate-600">Período</p>
                                      <p className="text-sm font-bold text-purple-600">
                                        {transacao.detalhesPremiacao.periodo}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {transacao.detalhesPremiacao.observacoes && (
                                <div className="flex items-start gap-2 pt-2 border-t border-emerald-100">
                                  <i className="ri-information-fill text-teal-600 text-sm mt-0.5"></i>
                                  <p className="text-xs text-slate-700 italic">
                                    {transacao.detalhesPremiacao.observacoes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Saldo */}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>Pontos Disponíveis:</span>
                          <span className="font-medium text-slate-900">
                            {transacao.saldoAtual.toLocaleString('pt-BR')} pts
                          </span>
                          {(transacao.tipo === 'ganho' || transacao.tipo === 'bonus') && (() => {
                            // Mapeia transações imutáveis para seus creditoIds
                            const creditoMap: Record<number, string> = {
                              999999: 'credito-1',
                              999998: 'credito-2',
                            };
                            const creditoId = creditoMap[transacao.id];
                            const url = creditoId
                              ? `/premiado/recompensas?aba=resgates&creditoId=${creditoId}`
                              : `/premiado/recompensas?aba=resgates`;
                            return (
                              <button
                                onClick={() => navigate(url)}
                                title="Ver débitos relacionados em Meus Resgates"
                                className="w-5 h-5 flex items-center justify-center text-teal-500 hover:text-teal-700 transition-colors cursor-pointer"
                              >
                                <i className="ri-external-link-line text-sm"></i>
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rodapé de Paginação */}
        {transacoesFiltradas.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Info + itens por página */}
            <div className="flex items-center gap-4">
              <p className="text-sm text-slate-600">
                Exibindo{' '}
                <span className="font-semibold text-slate-900">{indiceInicio + 1}</span>
                {' '}–{' '}
                <span className="font-semibold text-slate-900">{Math.min(indiceFim, transacoesFiltradas.length)}</span>
                {' '}de{' '}
                <span className="font-semibold text-slate-900">{transacoesFiltradas.length}</span>
                {' '}transações
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 whitespace-nowrap">Por página:</span>
                <select
                  value={itensPorPagina}
                  onChange={e => setItensPorPagina(Number(e.target.value))}
                  className="px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white"
                >
                  {ITENS_POR_PAGINA_OPCOES.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Controles de navegação */}
            <div className="flex items-center gap-1">
              {/* Primeira página */}
              <button
                onClick={() => irParaPagina(1)}
                disabled={paginaAtual === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Primeira página"
              >
                <i className="ri-skip-back-mini-line text-base"></i>
              </button>

              {/* Anterior */}
              <button
                onClick={() => irParaPagina(paginaAtual - 1)}
                disabled={paginaAtual === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Página anterior"
              >
                <i className="ri-arrow-left-s-line text-base"></i>
              </button>

              {/* Reticências início */}
              {getPaginasVisiveis()[0] > 1 && (
                <>
                  <button
                    onClick={() => irParaPagina(1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
                  >
                    1
                  </button>
                  {getPaginasVisiveis()[0] > 2 && (
                    <span className="w-8 h-8 flex items-center justify-center text-slate-400 text-sm">…</span>
                  )}
                </>
              )}

              {/* Páginas visíveis */}
              {getPaginasVisiveis().map(p => (
                <button
                  key={p}
                  onClick={() => irParaPagina(p)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    p === paginaAtual
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p}
                </button>
              ))}

              {/* Reticências fim */}
              {getPaginasVisiveis()[getPaginasVisiveis().length - 1] < totalPaginas && (
                <>
                  {getPaginasVisiveis()[getPaginasVisiveis().length - 1] < totalPaginas - 1 && (
                    <span className="w-8 h-8 flex items-center justify-center text-slate-400 text-sm">…</span>
                  )}
                  <button
                    onClick={() => irParaPagina(totalPaginas)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
                  >
                    {totalPaginas}
                  </button>
                </>
              )}

              {/* Próxima */}
              <button
                onClick={() => irParaPagina(paginaAtual + 1)}
                disabled={paginaAtual === totalPaginas}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Próxima página"
              >
                <i className="ri-arrow-right-s-line text-base"></i>
              </button>

              {/* Última página */}
              <button
                onClick={() => irParaPagina(totalPaginas)}
                disabled={paginaAtual === totalPaginas}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Última página"
              >
                <i className="ri-skip-forward-mini-line text-base"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Informações Adicionais */}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <i className="ri-information-fill text-2xl text-teal-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">
              Sobre seus pontos
            </h3>
            <ul className="space-y-1 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Pontos ganhos em missões e jogos são creditados instantaneamente</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Pontos expiram após 360 dias sem utilização</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Resgates são processados em até 2 horas úteis</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>XP ganho contribui para seu nível e ranking na campanha</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Botões de Exportação */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={exportarPDF}
          className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2"
          aria-label="Exportar extrato em PDF"
        >
          <i className="ri-file-pdf-line text-xl"></i>
          Exportar PDF
        </button>
        <button
          onClick={exportarExcel}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2"
          aria-label="Exportar extrato em Excel"
        >
          <i className="ri-file-excel-line text-xl"></i>
          Exportar Excel
        </button>
        <button
          onClick={() => setShowGrafico(!showGrafico)}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2"
          aria-label="Ver gráfico de evolução"
        >
          <i className="ri-line-chart-line text-xl"></i>
          {showGrafico ? 'Ocultar' : 'Ver'} Gráfico
        </button>
        <button
          onClick={() => setShowComparacao(!showComparacao)}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2"
          aria-label="Ver comparação mensal"
        >
          <i className="ri-bar-chart-grouped-line text-xl"></i>
          {showComparacao ? 'Ocultar' : 'Ver'} Comparação
        </button>
      </div>

      {/* Gráfico de Evolução */}
      {showGrafico && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-6">Evolução de Pontos (Últimos 6 Meses)</h3>
          <div className="relative h-64">
            {/* Grid de fundo */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="border-t border-slate-100"></div>
              ))}
            </div>

            {/* Barras do gráfico */}
            <div className="absolute inset-0 flex items-end justify-around px-4">
              {dadosGrafico().map((dado, index) => (
                <div key={index} className="flex flex-col items-center gap-2 flex-1 max-w-[80px]">
                  <div className="w-full flex gap-1 items-end h-48">
                    <div className="relative flex-1 group">
                      <div
                        className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg transition-all hover:from-teal-600 hover:to-teal-500 cursor-pointer"
                        style={{ height: `${(dado.pontos / maxPontos) * 100}%` }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-2 py-1 rounded text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {dado.pontos.toLocaleString('pt-BR')} pts
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-slate-600">{dado.mes}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Comparação Mensal */}
      {showComparacao && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-6">Comparação Mensal</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-600">Mês Atual</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    {comparacao.pontosAtual.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-500 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-600">Mês Anterior</h4>
                  <p className="text-2xl font-bold text-slate-600">
                    {comparacao.pontosAnterior.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>

            <div className={`bg-gradient-to-br ${comparacao.variacao >= 0 ? 'from-green-50 to-green-100 border-green-200' : 'from-red-50 to-red-100 border-red-200'} rounded-xl p-6 border`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 ${comparacao.variacao >= 0 ? 'bg-green-500' : 'bg-red-500'} rounded-lg flex items-center justify-center`}>
                  <i className={`${comparacao.variacao >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-2xl text-white`}></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-600">Variação</h4>
                  <p className={`text-2xl font-bold ${comparacao.variacao >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {comparacao.variacao >= 0 ? '+' : ''}{comparacao.variacao.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={`mt-6 p-4 ${comparacao.variacao >= 0 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'} border rounded-lg`}>
            <p className={`text-sm ${comparacao.variacao >= 0 ? 'text-green-800' : 'text-orange-800'} flex items-center gap-2`}>
              <i className={`${comparacao.variacao >= 0 ? 'ri-thumb-up-line' : 'ri-information-line'} text-lg`}></i>
              {comparacao.variacao >= 0 ? (
                <span><strong>Parabéns!</strong> Você ganhou mais pontos este mês em comparação ao mês anterior.</span>
              ) : (
                <span><strong>Atenção:</strong> Seus ganhos diminuíram em relação ao mês anterior. Continue se esforçando!</span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
